/**
 * HYPSEYA — Account Settings
 * GDPR data rights: export, correction, deletion + account info
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth0 as useAuth0Real } from '@auth0/auth0-react';
import { useAuth0 as useAuth0Mock } from '@/MockAuth0Provider';
const useAuth0 = process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ? useAuth0Mock : useAuth0Real;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, subtitle, children }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.9)',
      borderRadius: '18px',
      padding: '24px',
      border: '1.5px solid rgba(184,211,207,0.3)',
      marginBottom: '14px',
    }}>
      <p style={{
        fontSize: '0.7rem', fontWeight: '700', letterSpacing: '0.14em',
        textTransform: 'uppercase', color: '#2D8B87', margin: '0 0 4px',
      }}>{title}</p>
      {subtitle && (
        <p style={{ fontSize: '0.82rem', color: '#8FA3A0', margin: '0 0 18px', lineHeight: 1.5 }}>
          {subtitle}
        </p>
      )}
      {children}
    </div>
  );
}

// ── Action button ────────────────────────────────────────────────────────────
function ActionButton({ label, sublabel, onClick, variant = 'default', loading, disabled }) {
  const colors = {
    default: { bg: 'rgba(30,107,104,0.07)', text: '#1E6B68', border: 'rgba(30,107,104,0.2)' },
    danger:  { bg: 'rgba(200,60,60,0.06)',   text: '#C83C3C', border: 'rgba(200,60,60,0.2)' },
  };
  const c = colors[variant];
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        width: '100%', padding: '14px 18px',
        background: c.bg, border: `1.5px solid ${c.border}`,
        borderRadius: '14px', cursor: (disabled || loading) ? 'not-allowed' : 'pointer',
        textAlign: 'left', opacity: (disabled || loading) ? 0.6 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: c.text }}>
        {loading ? 'Please wait…' : label}
      </p>
      {sublabel && (
        <p style={{ margin: '3px 0 0', fontSize: '0.78rem', color: '#8FA3A0', lineHeight: 1.4 }}>
          {sublabel}
        </p>
      )}
    </button>
  );
}

// ── Research Mode (data donation) ────────────────────────────────────────────
const RESEARCH_SCOPES = [
  { key: 'assessment_scores', label: 'Validated scale scores (PHQ-9, GAD-7, EPDS, MEQ-30, EBI) — never your individual answers' },
  { key: 'eri_trajectory', label: 'Your ERI trajectory over time' },
  { key: 'practice_metadata', label: 'Practice frequency and protocol phases — never journal content' },
  { key: 'integration_mood', label: 'Daily mood ratings (1–5) from integration protocols — never your words' },
];

function ResearchModeSection({ userId }) {
  const [consent, setConsent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API}/research/consent/${encodeURIComponent(userId)}`)
      .then(r => setConsent(r.data))
      .catch(() => setConsent({ consented: false }));
  }, [userId]);

  const toggle = async (value) => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/research/consent`, { user_id: userId, consented: value });
      setConsent(res.data);
      toast.success(value
        ? 'Thank you — your anonymised data now contributes to research.'
        : 'Consent withdrawn. Your data will not be included in future exports.');
    } catch {
      toast.error('Could not save your choice. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const consented = consent?.consented === true;

  return (
    <Section
      title="Research Mode"
      subtitle="Donate your anonymised outcome data to advance emotional health and psychedelic integration research. Entirely optional and revocable at any time."
    >
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '4px 0 14px',
      }}>
        <div>
          <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1E3A39' }}>
            {consented ? 'You are contributing to research' : 'Contribute anonymised data'}
          </p>
          {consented && consent?.consent_version && (
            <p style={{ margin: '3px 0 0', fontSize: '0.72rem', color: '#8FA3A0' }}>
              Consent v{consent.consent_version} · {consent.created_at?.slice(0, 10)}
            </p>
          )}
        </div>
        <button
          onClick={() => toggle(!consented)}
          disabled={saving || consent === null}
          aria-label="Toggle research consent"
          style={{
            width: 52, height: 30, borderRadius: 100, border: 'none', cursor: 'pointer',
            background: consented ? '#1E6B68' : 'rgba(184,211,207,0.5)',
            position: 'relative', transition: 'background 0.2s', flexShrink: 0,
            opacity: saving ? 0.6 : 1,
          }}
        >
          <span style={{
            position: 'absolute', top: 3, left: consented ? 25 : 3,
            width: 24, height: 24, borderRadius: '50%', background: '#fff',
            transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>

      <button
        onClick={() => setShowDetails(s => !s)}
        style={{
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: '0.8rem', color: '#2D8B87', fontWeight: 600,
        }}
      >
        {showDetails ? 'Hide details' : 'What exactly is shared?'}
      </button>

      {showDetails && (
        <div style={{ marginTop: 12 }}>
          {RESEARCH_SCOPES.map(s => (
            <div key={s.key} style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
              <span style={{ color: '#1E6B68', fontSize: '0.8rem', marginTop: 1 }}>✓</span>
              <p style={{ margin: 0, fontSize: '0.8rem', color: '#4A5E5D', lineHeight: 1.5 }}>{s.label}</p>
            </div>
          ))}
          <p style={{ margin: '12px 0 0', fontSize: '0.75rem', color: '#8FA3A0', lineHeight: 1.6 }}>
            Your identity is replaced by an anonymous participant code before any export.
            Journal text, voice recordings and personal details are never included.
            Withdrawing consent stops all future sharing (GDPR Art. 7).
          </p>
        </div>
      )}
    </Section>
  );
}

// ── Delete confirmation modal ─────────────────────────────────────────────────
function DeleteModal({ onConfirm, onCancel, loading }) {
  const [typed, setTyped] = useState('');
  const confirmed = typed.toLowerCase() === 'delete my account';
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(20,40,38,0.55)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        background: '#fff', borderRadius: '20px', padding: '28px',
        maxWidth: '360px', width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <p style={{
          fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.14em',
          textTransform: 'uppercase', color: '#C83C3C', margin: '0 0 12px',
        }}>Permanent Action</p>
        <h3 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: '1.3rem', color: '#1E3A39', margin: '0 0 12px',
        }}>Delete your account?</h3>
        <p style={{ fontSize: '0.85rem', color: '#4A5E5D', lineHeight: 1.6, margin: '0 0 20px' }}>
          This will permanently delete all your data — Daily Loop history, ERI scores, meditations, voice model, and profile. This cannot be undone.
        </p>
        <p style={{ fontSize: '0.82rem', color: '#8FA3A0', margin: '0 0 10px' }}>
          Type <strong style={{ color: '#C83C3C' }}>delete my account</strong> to confirm:
        </p>
        <input
          type="text"
          value={typed}
          onChange={e => setTyped(e.target.value)}
          placeholder="delete my account"
          style={{
            width: '100%', padding: '12px 14px',
            border: '1.5px solid rgba(200,60,60,0.3)',
            borderRadius: '10px', fontSize: '0.88rem',
            outline: 'none', marginBottom: '18px',
            boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={onCancel}
            style={{
              flex: 1, padding: '12px',
              background: 'rgba(184,211,207,0.2)',
              border: '1.5px solid rgba(184,211,207,0.4)',
              borderRadius: '12px', cursor: 'pointer',
              fontSize: '0.88rem', color: '#4A5E5D', fontWeight: '500',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={!confirmed || loading}
            style={{
              flex: 1, padding: '12px',
              background: confirmed ? '#C83C3C' : 'rgba(200,60,60,0.15)',
              border: 'none', borderRadius: '12px',
              cursor: confirmed && !loading ? 'pointer' : 'not-allowed',
              fontSize: '0.88rem', color: confirmed ? '#fff' : '#C83C3C',
              fontWeight: '600', transition: 'all 0.2s',
            }}
          >
            {loading ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AccountSettings() {
  const navigate = useNavigate();
  const { user, logout, getAccessTokenSilently } = useAuth0();

  const [exportLoading, setExportLoading]   = useState(false);
  const [deleteLoading, setDeleteLoading]   = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ── Export data ─────────────────────────────────────────────────────────────
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(`${API}/user/gdpr/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `hypseya-data-export-${new Date().toISOString().slice(0,10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Your data has been exported.');
    } catch (e) {
      console.error(e);
      toast.error('Export failed. Please try again or contact helena@hypseya.com');
    } finally {
      setExportLoading(false);
    }
  };

  // ── Delete account ──────────────────────────────────────────────────────────
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${API}/user/gdpr/delete-account`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Your account has been deleted.');
      setTimeout(() => {
        logout({ logoutParams: { returnTo: window.location.origin } });
      }, 1500);
    } catch (e) {
      console.error(e);
      toast.error('Deletion failed. Please contact helena@hypseya.com');
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(160deg, #F7F5F0 0%, #EAF1F0 100%)',
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Header */}
      <div style={{ padding: '32px 24px 20px', maxWidth: '600px', margin: '0 auto' }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#1E6B68', fontSize: '0.85rem', padding: 0,
            display: 'flex', alignItems: 'center', gap: '6px',
            marginBottom: '28px', fontWeight: '500',
          }}
        >
          ← Back
        </button>

        <span style={{
          fontSize: '0.68rem', fontWeight: '700', letterSpacing: '0.14em',
          textTransform: 'uppercase', color: '#2D8B87', display: 'block', marginBottom: '6px',
        }}>Account</span>
        <h1 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 'clamp(1.7rem, 5vw, 2.2rem)',
          color: '#1E3A39', margin: '0 0 4px', fontWeight: '500',
        }}>Settings</h1>
        <p style={{ color: '#8FA3A0', fontSize: '0.85rem', margin: 0 }}>
          Manage your account and personal data.
        </p>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '0 24px 80px' }}>

        {/* Account info */}
        <Section title="Your Account">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #1E6B68, #2D8B87)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: '1.1rem', fontWeight: '600', flexShrink: 0,
            }}>
              {user?.name?.[0]?.toUpperCase() || 'H'}
            </div>
            <div>
              <p style={{ margin: 0, fontWeight: '600', color: '#1E3A39', fontSize: '0.95rem' }}>
                {user?.name || 'Your name'}
              </p>
              <p style={{ margin: '2px 0 0', color: '#8FA3A0', fontSize: '0.82rem' }}>
                {user?.email || ''}
              </p>
            </div>
          </div>
        </Section>

        {/* Data rights */}
        <Section
          title="Your Data Rights"
          subtitle="Under GDPR, you have the right to access, export, and permanently delete all data HYPSEYA holds about you."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <ActionButton
              label="Export my data"
              sublabel="Downloads a JSON file with all your personal data — Art. 15 & 20 GDPR"
              onClick={handleExport}
              loading={exportLoading}
            />
            <ActionButton
              label="Correct my data"
              sublabel="Email us to correct inaccurate information — Art. 16 GDPR"
              onClick={() => window.open('mailto:helena@hypseya.com?subject=Data Correction Request&body=Hi Helena,%0A%0AI would like to correct the following data in my HYPSEYA account:%0A%0A[Please describe what needs to be corrected]%0A%0AAccount email: ' + (user?.email || ''), '_blank')}
            />
            <ActionButton
              label="Delete my account"
              sublabel="Permanently removes all your data from HYPSEYA — Art. 17 GDPR. Cannot be undone."
              onClick={() => setShowDeleteModal(true)}
              variant="danger"
              loading={deleteLoading}
            />
          </div>
        </Section>

        {/* Research Mode */}
        <ResearchModeSection userId={(() => {
          try { return JSON.parse(localStorage.getItem('user') || '{}').user_id || user?.sub; }
          catch { return user?.sub; }
        })()} />

        {/* Legal */}
        <Section title="Legal">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {[
              { label: 'Privacy Policy', to: '/privacy' },
              { label: 'Terms of Service', to: '/terms' },
            ].map(({ label, to }) => (
              <Link
                key={to}
                to={to}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 4px',
                  borderBottom: '1px solid rgba(184,211,207,0.25)',
                  textDecoration: 'none', color: '#1E3A39',
                  fontSize: '0.9rem',
                }}
              >
                <span>{label}</span>
                <span style={{ color: '#B8D3CF', fontSize: '0.85rem' }}>›</span>
              </Link>
            ))}
          </div>
        </Section>

        {/* Response time note */}
        <p style={{
          fontSize: '0.78rem', color: '#AAB8B6', textAlign: 'center',
          lineHeight: 1.6, margin: '8px 0 0',
        }}>
          Data requests are responded to within 30 days, as required by GDPR Art. 12.{'\n'}
          Contact: helena@hypseya.com
        </p>
      </div>

      {/* Delete modal */}
      {showDeleteModal && (
        <DeleteModal
          loading={deleteLoading}
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
}
